import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
    deleteAssessment,
    getAssessment,
    regenerateReport,
    rescoreAssessment,
} from '~/api/assessments'
import type { AdminAssessmentDetail, AdminAssessmentScore } from '~/api/types'
import { KRDS, type CategoryDatum } from '~/components/charts'
import { useToast } from '~/components/ui/toast'
import { useAuthStore } from '~/stores/auth-store'

// 인증평가 상세 화면의 상태·부수효과(상세 로드·리포트 재생성·재채점·삭제)와 파생값(달성률·
// 점수순 정렬·대분류별 달성률 집계)을 모은 훅. 페이지는 이 훅으로 오케스트레이션만 한다.
export function useAssessmentDetail() {
    const { id = '' } = useParams()
    const navigate = useNavigate()
    const toast = useToast()
    const adminRole = useAuthStore((s) => s.adminRole)
    const isSuperadmin = adminRole === 'superadmin'
    const canManage = adminRole === 'admin' || isSuperadmin
    const [detail, setDetail] = useState<AdminAssessmentDetail | null>(null)
    const [loading, setLoading] = useState(true)
    // 진행 중인 운영 제어(중복 클릭 방지·버튼별 스피너). null이면 유휴.
    const [busy, setBusy] = useState<
        'regenerate' | 'rescore' | 'delete' | null
    >(null)
    const [rescoreConfirm, setRescoreConfirm] = useState(false)
    const [deleteConfirm, setDeleteConfirm] = useState(false)

    // scored/reported 일 때만 재생성·재채점 활성(collecting 등은 비활성).
    const canScoreOps =
        detail?.status === 'scored' || detail?.status === 'reported'

    // 리포트 재생성: 비파괴적이라 확인 없이 실행하되 느림을 버튼으로 안내. 완료 후 상세 refetch.
    async function handleRegenerate() {
        setBusy('regenerate')
        try {
            await regenerateReport(id)
            setDetail(await getAssessment(id))
            toast.success('리포트를 재생성했어요.')
        } catch (e) {
            toast.error((e as Error).message)
        } finally {
            setBusy(null)
        }
    }

    // 재채점: 점수가 바뀌는 작업이라 ConfirmDialog 후 실행. 완료 후 상세 refetch.
    async function handleRescore() {
        setBusy('rescore')
        try {
            await rescoreAssessment(id)
            setDetail(await getAssessment(id))
            toast.success('재채점을 완료했어요.')
        } catch (e) {
            toast.error((e as Error).message)
        } finally {
            setBusy(null)
        }
    }

    // 삭제: 영구 작업이라 ConfirmDialog 후 실행. 성공 시 목록으로 이동.
    async function handleDelete() {
        setBusy('delete')
        try {
            await deleteAssessment(id)
            toast.success('평가를 삭제했어요.')
            navigate('/certification')
        } catch (e) {
            toast.error((e as Error).message)
            setBusy(null)
        }
    }

    useEffect(() => {
        if (!id) return
        let alive = true
        setLoading(true)
        getAssessment(id)
            .then((data) => {
                if (alive) setDetail(data)
            })
            .catch((e) => {
                if (alive) toast.error((e as Error).message)
            })
            .finally(() => {
                if (alive) setLoading(false)
            })
        return () => {
            alive = false
        }
    }, [id, toast])

    const ratio =
        detail && detail.maxScore != null && detail.maxScore > 0
            ? (detail.totalScore ?? 0) / detail.maxScore
            : null
    const pct = ratio != null ? Math.round(ratio * 100) : null

    // 점수순(달성률 오름차순) 정렬 — 취약 항목을 먼저 노출해 보완 우선순위를 드러낸다.
    const sortedScores = useMemo(() => {
        const scores = detail?.scores ?? []
        const scoreRatio = (s: AdminAssessmentScore) =>
            s.maxScore != null && s.maxScore > 0
                ? (s.score ?? 0) / s.maxScore
                : 1
        return [...scores].sort((a, b) => scoreRatio(a) - scoreRatio(b))
    }, [detail])

    // 대분류별 달성률(%) 집계 — 분포 차트용. majorCategory 가 있는 항목만.
    const categoryData: CategoryDatum[] = useMemo(() => {
        const agg = new Map<string, { score: number; max: number }>()
        for (const s of detail?.scores ?? []) {
            if (!s.majorCategory || s.maxScore == null) continue
            const cur = agg.get(s.majorCategory) ?? { score: 0, max: 0 }
            cur.score += s.score ?? 0
            cur.max += s.maxScore
            agg.set(s.majorCategory, cur)
        }
        return [...agg.entries()]
            .filter(([, v]) => v.max > 0)
            .map(([name, v]) => {
                const r = v.score / v.max
                return {
                    name,
                    value: Math.round(r * 100),
                    color:
                        r >= 0.8
                            ? KRDS.success
                            : r >= 0.5
                              ? KRDS.warning
                              : KRDS.danger,
                }
            })
    }, [detail])

    return {
        navigate,
        detail,
        loading,
        busy,
        rescoreConfirm,
        setRescoreConfirm,
        deleteConfirm,
        setDeleteConfirm,
        isSuperadmin,
        canManage,
        canScoreOps,
        handleRegenerate,
        handleRescore,
        handleDelete,
        ratio,
        pct,
        sortedScores,
        categoryData,
    }
}
